import React, { useEffect, useState } from 'react';
import { apiFetch } from '../api';
import PlanFactTable from '../components/PlanFactTable';
import SalesSummaryTable from '../components/SalesSummaryTable';
import DeliveryModal from '../components/DeliveryModal';
import SkuFinanceTable from '../components/SkuFinanceTable';
import PromoByOrdersBlock from '../components/PromoByOrdersBlock';
import QueryStats from '../components/QueryStats';
import ProductFinanceTable from '../components/ProductFinanceTable';

interface Product {
  id: number;
  name: string;
  alias?: string;
  sku: string;
}

const Dashboard: React.FC = () => {
  const [day, setDay] = useState({
    planAmount: 0,
    planQty: 0,
    factAmount: 0,
    factQty: 0,
    monthPlanAmount: 0,
    monthFactAmount: 0,
  });
  const [month, setMonth] = useState({
    planAmount: 0,
    planQty: 0,
    factAmount: 0,
    factQty: 0,
    forecastAmount: 0,
  });
  const [balance, setBalance] = useState({ current: 0, plus: 0, minus: 0 });
  const [next, setNext] = useState({ date: '', items: 0, boxes: 0 });
  const [openDeliveries, setOpenDeliveries] = useState(false);
  const [deliveryInfo, setDeliveryInfo] = useState<{
    date: string;
    items: { name: string; quantity: number; shipments: number }[];
    total: number;
  }[]>([]);
  const data = {
    yesterday: day,
    month:    month,
    next:     next,
    balance:  balance
  };

  useEffect(() => {
    async function load() {
      const resDay = await apiFetch('/api/dashboard/day');
      if (resDay.ok) {
        const json = await resDay.json();
        setDay(json);
      }
      const resMonth = await apiFetch('/api/dashboard/month');
      if (resMonth.ok) {
        const json = await resMonth.json();
        setMonth(json);
      }
      const resBalance = await apiFetch('/api/dashboard/balance');
      if (resBalance.ok) {
        const json = await resBalance.json();
        setBalance(json);
      }
      const resNext = await apiFetch('/api/dashboard/next-delivery');
      if (resNext.ok) {
        const json = await resNext.json();
        setNext(json);
      }
    }
    load().catch(console.error);
    const id = setInterval(() => {
      load().catch(console.error);
    }, 60000);
    return () => clearInterval(id);
  }, []);

  const formatNumber = (num: number) => {
    const hasFraction = Math.abs(num % 1) > 0;
    const fractionDigits = hasFraction ? 2 : 0;
    return new Intl.NumberFormat('ru-RU', {
      minimumFractionDigits: fractionDigits,
      maximumFractionDigits: fractionDigits,
    }).format(num);
  };

  const progressDay = data.yesterday.planAmount
    ? (data.yesterday.factAmount / data.yesterday.planAmount) * 100
    : 0;
  const progressMonth = month.planAmount
    ? (month.factAmount / month.planAmount) * 100
    : 0;

  const getProgressColor = (value: number) => {
    if (value <= 100) return 'bg-blue-500';
    const shades = [
      'bg-green-100',
      'bg-green-200',
      'bg-green-300',
      'bg-green-400',
      'bg-green-500',
      'bg-green-600',
      'bg-green-700',
      'bg-green-800',
    ];
    const index = Math.min(Math.floor((value - 100) / 15), shades.length - 1);
    return shades[index];
  };

  const clampProgress = (value: number) => Math.min(value, 100);

  const diffDays = (() => {
    if (!data.next.date) return 0;
    const parts = data.next.date.split('.');
    const dt = new Date(+parts[2], +parts[1] - 1, +parts[0]);
    const now = new Date();
    return Math.ceil((dt.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
  })();

  const supplyDateClass = diffDays === 1
    ? 'text-red-600'
    : diffDays < 3
      ? 'text-orange-500'
      : diffDays < 7
        ? 'text-green-600'
        : 'text-black';

  const toggleDeliveries = async () => {
    if (!openDeliveries) {
      const [res, prodRes] = await Promise.all([
        apiFetch('/api/dashboard/next-deliveries'),
        apiFetch('/api/products'),
      ]);
      if (res.ok && prodRes.ok) {
        const [json, products]: [any, Product[]] = await Promise.all([
          res.json(),
          prodRes.json(),
        ]);
        const prodMap = new Map(
          products.map(p => [p.sku, p.alias && p.alias.trim() ? p.alias : p.name])
        );
        const mapped = json.map((d: any) => ({
          ...d,
          items: d.items.map((item: any) => {
            const sku = item.name;
            const label = prodMap.get(sku) || item.name;
            return { ...item, name: label };
          }),
        }));
        setDeliveryInfo(mapped);
      } else if (res.ok) {
        const json = await res.json();
        setDeliveryInfo(json);
      }
    }
    setOpenDeliveries(!openDeliveries);
  };

  return (
    <div className="bg-gray-100 p-6 min-h-screen">
      <div className="max-w-7xl mx-auto bg-white rounded-2xl shadow p-6 mb-8">
        <div className="mb-4"></div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {/* Day Tile */}
          <div className="bg-gray-50 p-4 rounded-xl hover:shadow-lg hover:-translate-y-1 transition-transform duration-200">
            <h3 className="text-lg font-semibold text-gray-700">День</h3>
            <div className="mt-4">
              <div className={`text-4xl font-bold text-center ${data.yesterday.factAmount >= data.yesterday.planAmount ? 'text-green-600' : 'text-red-600'}`}>{formatNumber(data.yesterday.factAmount)} ₽</div>
              <div className={`text-lg text-right ${data.yesterday.factQty >= data.yesterday.planQty ? 'text-green-600' : 'text-red-600'}`}>{formatNumber(data.yesterday.factQty)} шт.</div>
              <div className="mt-2 bg-gray-200 h-2 rounded-full">
                <div
                  className={`${getProgressColor(progressDay)} h-2 rounded-full`}
                  style={{ width: clampProgress(progressDay) + '%' }}
                ></div>
              </div>
              <div className="mt-2 flex justify-between text-base font-medium text-gray-600">
                <span className="text-left">{formatNumber(data.yesterday.planAmount)} ₽</span>
                <span className="text-right">{formatNumber(data.yesterday.planQty)} шт.</span>
              </div>
            </div>
          </div>

          {/* Month Tile */}
          <div className="bg-gray-50 p-4 rounded-xl hover:shadow-lg hover:-translate-y-1 transition-transform duration-200">
            <h3 className="text-lg font-semibold text-gray-700">Месяц</h3>
            <div className="mt-4">
              <div className={`text-4xl font-bold text-center ${data.month.factAmount >= data.month.planAmount ? 'text-green-600' : 'text-red-600'}`}>{formatNumber(data.month.factAmount)} ₽</div>
              <div className={`text-lg text-right ${data.month.factQty >= data.month.planQty ? 'text-green-600' : 'text-red-600'}`}>{formatNumber(data.month.factQty)} шт.</div>
              <div className="mt-2 bg-gray-200 h-2 rounded-full">
                <div
                  className={`${getProgressColor(progressMonth)} h-2 rounded-full`}
                  style={{ width: clampProgress(progressMonth) + '%' }}
                ></div>
              </div>
              <div className="mt-2 flex justify-between text-base font-medium text-gray-600">
                <span className="text-left">{formatNumber(data.month.planAmount)} ₽</span>
                <span className="text-right">{formatNumber(data.month.planQty)} шт.</span>
              </div>
              <div className={`mt-1 text-sm font-medium text-left ${data.month.forecastAmount >= data.month.planAmount ? 'text-green-600' : 'text-red-600'}`}>{formatNumber(Math.round(data.month.forecastAmount))} ₽</div>
            </div>
          </div>

          {/* Supply Tile */}
          <div
            className="bg-gray-50 p-4 rounded-xl hover:shadow-lg hover:-translate-y-1 transition-transform duration-200 cursor-pointer"
            onClick={toggleDeliveries}
          >
            <h3 className="text-lg font-semibold text-gray-700">Поставка</h3>
            <div className="mt-4 text-gray-800">
              <div className={`text-4xl font-medium mb-6 text-center ${supplyDateClass}`}>{data.next.date}</div>
              <div className="text-lg mb-2 text-right">{formatNumber(data.next.items)} наименований</div>
              <div className="text-lg text-right">{formatNumber(data.next.boxes)} коробок</div>
            </div>
          </div>

          {/* Balance Tile */}
          <div className="bg-gray-50 p-4 rounded-xl hover:shadow-lg hover:-translate-y-1 transition-transform duration-200">
            <h3 className="text-lg font-semibold text-gray-700">Баланс</h3>
            <div className="mt-4">
              <div className="text-4xl font-bold text-black text-center mb-6">{formatNumber(data.balance.current)} ₽</div>
              <div className="flex justify-center text-lg">
                <span className="text-green-600 font-medium">+{formatNumber(data.balance.plus)} ₽</span>
                <span className="text-red-600 ml-2">-{formatNumber(data.balance.minus)} ₽</span>
              </div>
            </div>
          </div>
        </div>
      </div>
      <PromoByOrdersBlock />
      <ProductFinanceTable />
      <PlanFactTable />
      <SalesSummaryTable />
      <SkuFinanceTable />
      <QueryStats />
      {openDeliveries && (
        <DeliveryModal data={deliveryInfo} onClose={() => setOpenDeliveries(false)} />
      )}
    </div>
  );
};

export default Dashboard;
